import React from "react";
import { Navigate, useNavigate, Link } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { GraduationCap, KeyRound, ArrowRight, Building2 } from "lucide-react";
import WishireLogo from "@/components/wishire/WishireLogo";

/* Closed platform: Wishire accounts are created by the college's placement
   cell (bulk import / invites) or the Wishire admin — there is no public
   self-signup. This page tells arrivals how to actually get in. The backend
   enforces the same rule (signup + OAuth auto-creation both refuse), gated by
   the admin's self_signup_enabled feature flag. */

export default function Signup() {
  const navigate = useNavigate();
  const { user } = useAuth();
  if (user) return <Navigate to="/app" replace />;

  return (
    <div className="min-h-screen bg-[#FDFBF7] text-stone-900 flex items-center justify-center px-5 py-14">
      <div className="w-full max-w-lg">
        <div className="mb-8 flex justify-center">
          <Link to="/"><WishireLogo size="md" /></Link>
        </div>

        <div className="rounded-3xl border border-stone-200 bg-white p-8 md:p-10 text-center" data-testid="signup-closed">
          <div className="inline-flex p-4 rounded-2xl bg-amber-50 border border-amber-200 text-[#C2410C]">
            <KeyRound size={26} />
          </div>
          <h1 className="font-display text-3xl mt-5 leading-tight">
            Your college creates your account.
          </h1>
          <p className="mt-3 text-stone-600 leading-relaxed">
            Wishire is invite-only right now. Your placement cell registers your
            email — then you sign in and complete your profile on first login.
          </p>

          <div className="mt-7 space-y-3 text-left">
            <div className="flex items-start gap-3 rounded-2xl bg-[#F5F3ED] px-4 py-3.5">
              <GraduationCap size={18} className="shrink-0 mt-0.5 text-[#C2410C]" />
              <p className="text-sm text-stone-700">
                <b>Students:</b> if your college is on Wishire, sign in with your
                registered email — a login code goes to your inbox. Not registered
                yet? Ask your placement cell to add you.
              </p>
            </div>
            <div className="flex items-start gap-3 rounded-2xl bg-[#F5F3ED] px-4 py-3.5">
              <Building2 size={18} className="shrink-0 mt-0.5 text-[#C2410C]" />
              <p className="text-sm text-stone-700">
                <b>Colleges & companies:</b> we onboard you personally —
                book a demo and your workspace is live the same day.
              </p>
            </div>
          </div>

          <button
            onClick={() => navigate("/login")}
            data-testid="signup-goto-login"
            className="mt-8 w-full inline-flex items-center justify-center gap-2 px-5 py-3.5 rounded-full bg-stone-900 text-stone-50 font-medium hover:bg-[#C2410C] transition-colors"
          >
            Sign in with your registered email <ArrowRight size={15} />
          </button>
          <a
            href="/#book-demo"
            className="mt-3 inline-block text-sm font-medium text-stone-600 underline underline-offset-4 hover:text-[#C2410C]"
          >
            Bring Wishire to your college — book a demo
          </a>
        </div>
      </div>
    </div>
  );
}
